import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CircleNotch, Trash, Copy, ArrowClockwise, Browser, CheckCircle, WarningCircle, Clock, Globe } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;
const T = '#00A0D2';

const STATUS_LABEL = {
  queued: 'Queued', cf_zone: 'Creating Cloudflare zone', ns_set: 'Setting nameservers',
  dns_set: 'Configuring DNS', awaiting_cpanel: 'Add domain in cPanel', wp_installing: 'Installing WordPress',
  wp_installed: 'WordPress installed', optimizing: 'Optimizing', ai_building: 'AI building site', live: 'Live', failed: 'Failed',
};
const STATUS_COLOR = {
  live: 'text-[#00E676] border-[#00E676]/40', wp_installed: 'text-[#00E676] border-[#00E676]/40',
  failed: 'text-red-400 border-red-400/40', awaiting_cpanel: 'text-amber-400 border-amber-400/40',
};
const stepIcon = (st) => st === 'done' ? <CheckCircle size={15} className="text-[#00E676]" /> : st === 'error' ? <WarningCircle size={15} className="text-red-400" /> : st === 'waiting' ? <Clock size={15} className="text-amber-400" /> : <CircleNotch size={15} className="animate-spin text-slate-400" />;

const WPBSites = () => {
  const [sites, setSites] = useState([]);
  const [busy, setBusy] = useState('');
  const pollRef = useRef(null);
  const load = async () => { try { const r = await axios.get(`${API}/api/wp-builder/sites`, { withCredentials: true }); setSites(r.data); } catch {} };
  useEffect(() => { load(); pollRef.current = setInterval(load, 6000); return () => clearInterval(pollRef.current); }, []);

  const copy = (t) => { navigator.clipboard.writeText(t); toast.success('Copied'); };
  const retry = async (id) => { setBusy(id + 'r'); try { await axios.post(`${API}/api/wp-builder/sites/${id}/retry`, {}, { withCredentials: true }); toast.success('Retrying'); load(); } catch { toast.error('Failed'); } finally { setBusy(''); } };
  const installWp = async (id) => { setBusy(id + 'wp'); try { await axios.post(`${API}/api/wp-builder/sites/${id}/install-wp`, {}, { withCredentials: true }); toast.success('Installing WordPress…'); load(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setBusy(''); } };
  const buildAi = async (id) => { setBusy(id + 'ai'); try { await axios.post(`${API}/api/wp-builder/sites/${id}/build-ai`, {}, { withCredentials: true }); toast.success('AI is building your site…'); load(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setBusy(''); } };
  const del = async (id) => { if (!window.confirm('Delete this site record? (does not delete the Cloudflare zone)')) return; setBusy(id + 'd'); try { await axios.delete(`${API}/api/wp-builder/sites/${id}`, { withCredentials: true }); toast.success('Deleted'); load(); } catch { toast.error('Failed'); } finally { setBusy(''); } };

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-500">
      <div><h1 className="text-4xl font-black mb-2 flex items-center gap-3" style={{ color: T }}><Browser size={38} weight="fill" /> WORDPRESS SITES</h1>
        <p className="text-slate-400 font-mono">Live build pipeline for each site.</p></div>

      <div className="grid gap-4">
        {sites.map(s => (
          <div key={s.id} className="bg-card border border-white/10 p-6" data-testid={`wpb-site-${s.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="flex items-start gap-3"><Globe size={24} style={{ color: T }} className="mt-1" />
                <div>
                  <div className="font-bold text-white text-lg">{s.domain}</div>
                  <div className="text-xs text-slate-500 font-mono">{s.business_type} · A →{s.server_ip}</div>
                </div>
              </div>
              <span className={`px-2 py-1 text-xs font-bold border ${STATUS_COLOR[s.status] || 'text-slate-300 border-white/10'}`}>{STATUS_LABEL[s.status] || s.status}</span>
            </div>

            {s.nameservers && s.nameservers.length > 0 && (
              <div className="mt-3 text-xs font-mono text-slate-400 flex items-center gap-2 flex-wrap">
                <span className="text-slate-500 uppercase tracking-wider">Nameservers:</span>
                {s.nameservers.map(n => <span key={n} className="text-white">{n}</span>)}
                <button onClick={() => copy(s.nameservers.join('\n'))}><Copy size={12} className="text-slate-400 hover:text-white" /></button>
              </div>
            )}

            {s.status === 'awaiting_cpanel' && (
              <div className="mt-3 bg-amber-500/10 border border-amber-500/30 text-amber-200 p-3 text-xs">
                DNS is set. Add <span className="font-mono font-bold">{s.domain}</span> in cPanel (Create A New Domain), then click below to install WordPress.
                <div className="mt-2">
                  <button onClick={() => installWp(s.id)} disabled={busy === s.id + 'wp'} data-testid={`wpb-installwp-${s.id}`} className="flex items-center gap-1.5 text-white px-3 py-1.5 font-bold text-xs uppercase tracking-wider" style={{ background: T }}>{busy === s.id + 'wp' ? <CircleNotch size={14} className="animate-spin" /> : <ArrowClockwise size={14} />} Install WordPress</button>
                </div>
              </div>
            )}

            {/* step timeline */}
            {s.steps && s.steps.length > 0 && (
              <div className="mt-4 bg-[#0A0B0E] border border-white/10 p-3 space-y-1.5">
                {s.steps.map((st, i) => (
                  <div key={i} className="flex items-start gap-2 text-xs">
                    {stepIcon(st.status)}
                    <span className="text-slate-300 font-mono">{st.detail || st.key}</span>
                  </div>
                ))}
              </div>
            )}

            {s.status === 'failed' && s.error_msg && <div className="mt-3 text-xs text-red-400 font-mono bg-red-500/5 border border-red-500/20 p-2">{s.error_msg}</div>}

            {s.wp_admin_url && (
              <div className="mt-4 bg-[#0A0B0E] border border-white/10 p-3 text-sm space-y-1">
                <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-1">WordPress admin</div>
                <div className="font-mono text-white break-all">{s.wp_admin_url}</div>
                <div className="font-mono text-slate-300">{s.wp_user} / {s.wp_pass}</div>
                {(s.status === 'wp_installed' || s.status === 'live') && (
                  <div className="pt-2">
                    <button onClick={() => buildAi(s.id)} disabled={busy === s.id + 'ai'} data-testid={`wpb-buildai-${s.id}`} className="flex items-center gap-1.5 text-white px-3 py-1.5 font-bold text-xs uppercase tracking-wider" style={{ background: T }}>{busy === s.id + 'ai' ? <CircleNotch size={14} className="animate-spin" /> : <ArrowClockwise size={14} />} {s.status === 'live' ? 'Rebuild with AI' : 'Build with AI'}</button>
                    {s.status === 'live' && <a href={`https://${s.domain}`} target="_blank" rel="noreferrer" className="ml-2 inline-flex items-center gap-1 text-xs underline" style={{ color: T }}>View site ↗</a>}
                  </div>
                )}
              </div>
            )}

            <div className="mt-4 flex gap-2">
              {s.status === 'failed' && <button onClick={() => retry(s.id)} disabled={busy.startsWith(s.id)} data-testid={`wpb-retry-${s.id}`} className="flex items-center gap-1 border border-white/15 px-3 py-1.5 font-bold text-xs uppercase tracking-wider hover:bg-white/5">{busy === s.id + 'r' ? <CircleNotch size={14} className="animate-spin" /> : <ArrowClockwise size={14} />} Retry</button>}
              <button onClick={() => del(s.id)} disabled={busy.startsWith(s.id)} data-testid={`wpb-delsite-${s.id}`} className="flex items-center gap-1 bg-red-500/10 text-red-400 border border-red-500/20 px-3 py-1.5 font-bold text-xs uppercase tracking-wider hover:bg-red-500 hover:text-white"><Trash size={14} /> Delete</button>
            </div>
          </div>
        ))}
        {sites.length === 0 && <div className="text-slate-500 text-center py-10">No sites yet. Start one from “New Site”.</div>}
      </div>
    </div>
  );
};
export default WPBSites;
